/**
 * A drill's steps in the run-mode period sheet. Reads like the old numbered
 * list; tap a line to check it off (this device, this sheet only), and a line
 * whose call is linked to a play gets "view ›", which opens the play full screen
 * with pinch-zoom, ▶ Watch it run, and ‹ › to step through the script's plays.
 */
import { buildStandaloneSVG, type FrozenRep, type LibraryItemRow, type PlayData } from '@coach/core';
import { Modal } from '@coach/ui';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useApp } from '../../lib/app';
import { ZoomView } from '../drills/ZoomView';
import './scripts.css';

const PlayAnimator = lazy(() => import('../playbook/PlayAnimator').then((m) => ({ default: m.PlayAnimator })));

export function RunScript({ lines, asRun }: { lines: FrozenRep[]; asRun: boolean }) {
  const app = useApp();
  const [done, setDone] = useState<Set<number>>(() => new Set());
  const [plays, setPlays] = useState<Map<string, PlayData>>(new Map());
  const [viewing, setViewing] = useState<number | null>(null);
  const [watching, setWatching] = useState(false);
  const wanted = useMemo(() => [...new Set(lines.map((l) => l.play).filter(Boolean) as string[])], [lines]);
  useEffect(() => {
    let live = true;
    void Promise.all(wanted.map((id) => app.repos.library.get(id))).then((rows) => {
      if (!live) return;
      const m = new Map<string, PlayData>();
      for (const r of rows as (LibraryItemRow<PlayData> | undefined)[]) if (r && !r.deleted_at && r.kind === 'play') m.set(r.id, r.data);
      setPlays(m);
    });
    return () => {
      live = false;
    };
  }, [wanted, app.repos.library, app.syncStatus.lastSyncAt]);

  const viewable = lines.map((l, i) => (l.play && plays.has(l.play) ? i : -1)).filter((i) => i >= 0);
  const cur = viewing !== null ? lines[viewing] : undefined;
  const play = cur?.play ? plays.get(cur.play) : undefined;
  const svg = useMemo(() => (play ? buildStandaloneSVG(play, play.cardMode || 'skill') : ''), [play]);
  const step = (d: number) => {
    const k = viewable.indexOf(viewing ?? -1) + d;
    if (k >= 0 && k < viewable.length) setViewing(viewable[k]!);
  };
  const toggle = (i: number) => setDone((s) => {
    const n = new Set(s);
    if (n.has(i)) n.delete(i);
    else n.add(i);
    return n;
  });

  if (!lines.length) return null;
  const pos = viewable.indexOf(viewing ?? -1);
  return (
    <>
      <ol className="script-lines">
        {lines.map((l, i) => (
          <li key={i} className={'script-line' + (done.has(i) ? ' done' : '')} onClick={() => toggle(i)} data-testid="script-line">
            <span className="n">{i + 1}</span>
            <span className="t">{l.t}</span>
            {l.play && plays.has(l.play) && (
              <button className="btn btn-sm view" onClick={(e) => { e.stopPropagation(); setViewing(i); }}>view ›</button>
            )}
          </li>
        ))}
      </ol>
      {asRun && <div className="muted small" style={{ marginTop: 4, fontSize: '0.75em' }}>Script as it was run.</div>}
      {cur && play && !watching && (
        <ZoomView
          svg={svg}
          title={cur.t}
          onClose={() => setViewing(null)}
          extra={
            <>
              <button className="btn btn-sm" onClick={() => step(-1)} disabled={pos <= 0} aria-label="Previous play">‹</button>
              {play.lines.length > 0 && <button className="btn btn-sm btn-primary" onClick={() => setWatching(true)}>▶ Watch it run</button>}
              <button className="btn btn-sm" onClick={() => step(1)} disabled={pos >= viewable.length - 1} aria-label="Next play">›</button>
            </>
          }
        />
      )}
      {cur && play && watching && (
        <Modal open onClose={() => setWatching(false)} size="wide" title={cur.t} actions={<button className="btn" onClick={() => setWatching(false)}>Back</button>}>
          <Suspense fallback={<div className="spinner" />}>
            <PlayAnimator play={play} />
          </Suspense>
        </Modal>
      )}
    </>
  );
}
